import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $isElementNode,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
  RootNode,
} from "lexical";
import { useEffect, type RefObject } from "react";

interface SingleLinePluginProps {
  /**
   * When this ref is `true`, the merge tag typeahead menu is open and owns the
   * Enter key (Enter selects the highlighted suggestion). We must let that
   * lower-priority handler run, so we bow out instead of swallowing the key.
   */
  typeaheadOpenRef?: RefObject<boolean>;
}

export function SingleLinePlugin({
  typeaheadOpenRef,
}: SingleLinePluginProps = {}): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const unregisterEnter = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        // Defer to the typeahead's Enter-to-select handler when its menu is open.
        if (typeaheadOpenRef?.current) return false;
        event?.preventDefault();
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterTransform = editor.registerNodeTransform(
      RootNode,
      (rootNode) => {
        const children = rootNode.getChildren();
        if (children.length <= 1) return;

        const first = children[0];
        if (!$isElementNode(first)) return;

        for (let i = 1; i < children.length; i++) {
          const para = children[i];
          if ($isElementNode(para)) {
            for (const child of para.getChildren()) {
              first.append(child);
            }
          }
          para.remove();
        }
      },
    );

    return () => {
      unregisterEnter();
      unregisterTransform();
    };
  }, [editor, typeaheadOpenRef]);

  return null;
}
